/**
 * Form Storage Service
 * Handles saving and loading form progress to/from localStorage
 * Implements data security best practices - excludes sensitive fields
 */

import { SignupFormData } from '../types';

const STORAGE_KEY = 'smartr365_signup_progress';
const STORAGE_VERSION = '2.0';
const EXPIRATION_DAYS = 30;

/**
 * Saved form data structure
 */
export interface SavedFormData {
  version: string;
  savedAt: string;
  expiresAt: string;
  currentStep: number;
  data: Partial<SignupFormData>;
}

/**
 * Fields that should be saved to localStorage
 * EXCLUDES: phone (PII), acceptance checkboxes (must be re-accepted)
 */
const SAFE_FIELDS_TO_SAVE: (keyof SignupFormData)[] = [
  'firstName',
  'lastName',
  'email',
  'fcaNumber',
  'companyName',
  'companiesHouseNumber',
  'desiredSubdomain',
  'jobTitle',
  'companySize',
  'networkIds',
  'networkNames',
  'totangoId'
];

/**
 * Check if localStorage is available
 */
const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    console.warn('localStorage is not available:', error);
    return false;
  }
};

/**
 * Save form progress to localStorage
 * @param formData - Current form data
 * @param currentStep - Current step number (1-7)
 */
export const saveFormProgress = (formData: Partial<SignupFormData>, currentStep: number): boolean => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    // Filter data to only include safe fields
    const safeData: Partial<SignupFormData> = {};
    SAFE_FIELDS_TO_SAVE.forEach(field => {
      if (formData[field] !== undefined && formData[field] !== '' && formData[field] !== null) {
        // @ts-ignore - Dynamic field access
        safeData[field] = formData[field];
      }
    });

    const now = new Date();
    const expiresAt = new Date(now.getTime() + EXPIRATION_DAYS * 24 * 60 * 60 * 1000);

    const savedData: SavedFormData = {
      version: STORAGE_VERSION,
      savedAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      currentStep,
      data: safeData
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedData));
    console.log('=� Form progress saved to localStorage:', { currentStep, fieldsCount: Object.keys(safeData).length });
    return true;
  } catch (error) {
    console.error('Failed to save form progress:', error);
    return false;
  }
};

/**
 * Load saved form progress from localStorage
 * @returns SavedFormData if valid saved data exists, null otherwise
 */
export const loadFormProgress = (): SavedFormData | null => {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const savedDataString = localStorage.getItem(STORAGE_KEY);
    if (!savedDataString) {
      return null;
    }

    const savedData: SavedFormData = JSON.parse(savedDataString);

    // Check version compatibility
    if (savedData.version !== STORAGE_VERSION) {
      console.warn('Saved form data version mismatch, clearing old data');
      clearFormProgress();
      return null;
    }

    // Check expiration
    const now = new Date();
    const expiresAt = new Date(savedData.expiresAt);
    if (now > expiresAt) {
      console.warn('Saved form data expired, clearing');
      clearFormProgress();
      return null;
    }

    // Validate data structure
    if (!savedData.data || !savedData.currentStep) {
      console.warn('Invalid saved form data structure, clearing');
      clearFormProgress();
      return null;
    }

    console.log('=� Form progress loaded from localStorage:', {
      currentStep: savedData.currentStep,
      fieldsCount: Object.keys(savedData.data).length,
      savedAt: savedData.savedAt
    });

    return savedData;
  } catch (error) {
    console.error('Failed to load form progress:', error);
    clearFormProgress();
    return null;
  }
};

/**
 * Check if there is saved form progress
 * @returns true if valid saved progress exists
 */
export const hasSavedProgress = (): boolean => {
  return loadFormProgress() !== null;
};

/**
 * Clear saved form progress from localStorage
 */
export const clearFormProgress = (): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('=� Form progress cleared from localStorage');
  } catch (error) {
    console.error('Failed to clear form progress:', error);
  }
};

/**
 * Get the current step from saved progress
 * @returns Step number (1-7) or null if no saved progress
 */
export const getSavedStep = (): number | null => {
  const savedData = loadFormProgress();
  return savedData ? savedData.currentStep : null;
};

/**
 * Get saved form data
 * @returns Partial form data or null if no saved progress
 */
export const getSavedFormData = (): Partial<SignupFormData> | null => {
  const savedData = loadFormProgress();
  return savedData ? savedData.data : null;
};

/**
 * Update only the current step without changing saved form data
 * @param currentStep - Current step number
 */
export const updateSavedStep = (currentStep: number): boolean => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    const savedData = loadFormProgress();
    if (!savedData) {
      return false;
    }

    savedData.currentStep = currentStep;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedData));
    console.log('=� Saved step updated to:', currentStep);
    return true;
  } catch (error) {
    console.error('Failed to update saved step:', error);
    return false;
  }
};

/**
 * Get time until expiration
 * @returns Days until expiration, or null if no saved data
 */
export const getTimeUntilExpiration = (): number | null => {
  const savedData = loadFormProgress();
  if (!savedData) {
    return null;
  }

  const now = new Date();
  const expiresAt = new Date(savedData.expiresAt);
  const daysUntilExpiration = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  return daysUntilExpiration;
};

const formStorageService = {
  saveFormProgress,
  loadFormProgress,
  hasSavedProgress,
  clearFormProgress,
  getSavedStep,
  getSavedFormData,
  updateSavedStep,
  getTimeUntilExpiration
};

export default formStorageService;
